import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, EmptyState } from "../components/ui-kit";
import { CalendarCheck2, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { thisMonthIST } from "../lib/datefmt";

const STATUS_META = {
  present: { bg: "bg-emerald-500", text: "P", label: "Present" },
  leave: { bg: "bg-red-500", text: "L", label: "Approved leave" },
  leave_pending: { bg: "bg-amber-400", text: "?", label: "Pending leave" },
  holiday: { bg: "bg-blue-500", text: "H", label: "Public holiday" },
  sunday: { bg: "bg-gray-300", text: "S", label: "Sunday off" },
  open: { bg: "bg-white border border-dashed border-[#D6D2C8]", text: "", label: "Open (no report)" },
};

export default function AttendancePage() {
  const [month, setMonth] = useState(() => thisMonthIST());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/attendance?month=${month}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [month]);

  const days = useMemo(() => (data ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : []), [data]);

  return (
    <div>
      <PageHeader
        title="Attendance"
        subtitle="Daily report submissions + approved leaves + public holidays; Sundays marked off"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-44"
              data-testid="attendance-month"
            />
            <Button
              variant="outline"
              data-testid="attendance-download-xlsx"
              onClick={async () => {
                try {
                  const res = await api.get(`/attendance.xlsx?month=${month}`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `attendance_${month}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("Download failed");
                }
              }}
            >
              <FileDown className="w-4 h-4" /> Excel
            </Button>
            <Button
              variant="outline"
              data-testid="attendance-download-pdf"
              onClick={async () => {
                try {
                  const res = await api.get(`/attendance.pdf?month=${month}`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `attendance_${month}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("Download failed");
                }
              }}
            >
              <FileText className="w-4 h-4" /> PDF
            </Button>
          </div>
        }
      />

      <Card className="mb-4">
        <div className="flex flex-wrap gap-4 text-xs">
          {Object.entries(STATUS_META).map(([k, meta]) => (
            <div key={k} className="flex items-center gap-2" data-testid={`legend-${k}`}>
              <span className={`inline-block w-4 h-4 rounded-sm ${meta.bg}`} />
              <span className="text-[#5C6B5E]">{meta.label}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading && !data ? (
          <div className="p-8 text-sm text-[#5C6B5E]">Loading…</div>
        ) : !data || data.users.length === 0 ? (
          <div className="p-8">
            <EmptyState icon={CalendarCheck2} title="No visible team members" description="Your reporting tree is empty for this month." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs" data-testid="attendance-grid">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-[#F9F8F6] z-10 min-w-[180px]">Team member</th>
                  {days.map((d) => {
                    const dt = new Date(`${month}-${String(d).padStart(2, "0")}`);
                    const isSun = dt.getDay() === 0;
                    return (
                      <th
                        key={d}
                        className={`px-1 py-2 text-center font-bold ${isSun ? "text-red-500" : "text-[#5C6B5E]"}`}
                      >
                        {d}
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-center bg-emerald-50 text-emerald-800">P</th>
                  <th className="px-2 py-2 text-center bg-red-50 text-red-800">L</th>
                  <th className="px-2 py-2 text-center bg-amber-50 text-amber-800">Open</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr
                    key={u.user_id}
                    className="border-b border-[#E5E2DC]"
                    data-testid={`attendance-row-${u.user_id}`}
                  >
                    <td className="px-3 py-2 sticky left-0 bg-white z-10 border-r border-[#E5E2DC]">
                      <div className="font-semibold">{u.user_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#5C6B5E]">
                        {(u.role || "").replace(/_/g, " ")}
                      </div>
                    </td>
                    {u.days.map((day) => {
                      const meta = STATUS_META[day.status] || STATUS_META.open;
                      const tip = `${day.date} — ${meta.label}${
                        day.mode ? ` (${day.mode})` : ""
                      }${day.holiday_name ? `: ${day.holiday_name}` : ""}`;
                      return (
                        <td key={day.date} className="p-0.5">
                          <div
                            title={tip}
                            className={`w-6 h-6 rounded-sm ${meta.bg} text-white text-[9px] font-bold flex items-center justify-center`}
                            data-testid={`att-cell-${u.user_id}-${day.date}`}
                          >
                            {meta.text}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center font-bold text-emerald-700">{u.totals.present}</td>
                    <td className="px-2 py-2 text-center font-bold text-red-600">{u.totals.leave}</td>
                    <td className="px-2 py-2 text-center font-bold text-amber-700">{u.totals.open}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
